export const EXPLORER_PALETTE = {
  style: 'lagoon', gScale: 7, aFixed: 255, useMu: true
};
export const PALETTE_SIZE = 512;
export const DEFAULT_PALETTE_THEME = 'amethyst';

export const PALETTE_THEMES = Object.freeze({
  amethyst: Object.freeze({
    id: 'amethyst',
    label: 'Amethyst Filigree',
    stops: Object.freeze([
      [12, 9, 28],
      [74, 52, 148],
      [146, 122, 222],
      [250, 240, 228],
      [238, 162, 96],
      [142, 46, 82],
    ]),
  }),
  celadon: Object.freeze({
    id: 'celadon',
    label: 'Celadon Scroll',
    stops: Object.freeze([
      [8, 22, 24],
      [22, 96, 86],
      [82, 184, 156],
      [245, 243, 230],
      [234, 148, 82],
      [136, 50, 44],
    ]),
  }),
});

// Interpolate in Oklab so brightness and hue evolve together. The luminous
// crest and warm inner inflection separate fine filaments from deep valleys.
const linear = value => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
const srgb = value => 255 * (value <= 0.0031308 ? 12.92 * value : 1.055 * value ** (1 / 2.4) - 0.055);

// A periodic cubic spline has matching slopes at every stop, including the
// wrap back to the nocturnal base stop. Build once; CPU, workers and GPU share the tables.
function buildPaletteTable(stops) {
  const labs = stops.map(color => {
    const [r, g, b] = color.map(value => linear(value / 255));
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
      0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
      1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
      0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
    ];
  });
  const table = new Uint8Array(PALETTE_SIZE * 4);
  for (let i = 0; i < PALETTE_SIZE; i++) {
    const position = i / PALETTE_SIZE * labs.length;
    const index = Math.floor(position);
    const t = position - index;
    const [L, a, b] = [0, 1, 2].map(channel => {
      const p0 = labs[(index + labs.length - 1) % labs.length][channel];
      const p1 = labs[index][channel];
      const p2 = labs[(index + 1) % labs.length][channel];
      const p3 = labs[(index + 2) % labs.length][channel];
      return 0.5 * (2 * p1 + (-p0 + p2) * t +
        (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
        (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
    });
    const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
    const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
    const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
    [
      4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
      -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
      -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
    ].forEach((value, channel) => {
      table[i * 4 + channel] = Math.round(Math.max(0, Math.min(255, srgb(value))));
    });
    table[i * 4 + 3] = 255;
  }
  return table;
}

export const paletteTables = Object.freeze({
  amethyst: buildPaletteTable(PALETTE_THEMES.amethyst.stops),
  celadon: buildPaletteTable(PALETTE_THEMES.celadon.stops),
});

export const paletteTable = paletteTables.amethyst;

export function getPaletteTable(params) {
  const theme = params?.theme || (params?.style === 'celadon' ? 'celadon' : DEFAULT_PALETTE_THEME);
  return paletteTables[theme] || paletteTable;
}

export function withPaletteTheme(palette, theme = DEFAULT_PALETTE_THEME) {
  if (!palette || typeof palette !== 'object') return palette;
  if (!theme || theme === DEFAULT_PALETTE_THEME) {
    if (!('theme' in palette)) return palette;
    const { theme: _removed, ...rest } = palette;
    return rest;
  }
  if (palette.theme === theme) return palette;
  return { ...palette, theme };
}

// Retained for callers of the original blue/yellow palette.
export function palettePosition(value, gScale = 7, repeat = false) {
  const position = value * gScale / 255;
  return repeat ? 0.5 - 0.5 * Math.cos(Math.PI * position) : Math.max(0, Math.min(1, position));
}

export function palettePhase(value, gScale = 7) {
  // Compress high escape counts gently, retaining separation without densely
  // packed stripes at the boundary. No cutoff: detail keeps receiving color.
  return (Math.sqrt(Math.max(0, value) + 1) - 1) * gScale / 55;
}

export function samplePalette(value, params, out, dem = 0) {
  if (params.style === 'lagoon' || params.style === 'amethyst' || params.style === 'celadon') {
    const table = getPaletteTable(params);
    const phase = palettePhase(value, params.gScale) + (params.phaseOffset ?? 0);
    const position = (phase - Math.floor(phase)) * PALETTE_SIZE;
    const index = Math.floor(position);
    const fraction = position - index;
    const base0 = index << 2;
    const base1 = ((index + 1) & (PALETTE_SIZE - 1)) << 2;
    const r0 = table[base0];
    const g0 = table[base0 + 1];
    const b0 = table[base0 + 2];
    out[0] = Math.round(r0 + (table[base1] - r0) * fraction);
    out[1] = Math.round(g0 + (table[base1 + 1] - g0) * fraction);
    out[2] = Math.round(b0 + (table[base1 + 2] - b0) * fraction);
  } else {
    const t = palettePosition(value, params.gScale, params.repeat);
    out[0] = out[1] = Math.round(255 * t);
    out[2] = Math.round(255 * (1 - t));
  }
  const demStrength = params?.demStrength;
  if (demStrength > 0 && dem > 0 && Number.isFinite(dem)) {
    const boost = Math.min(1, demStrength / (1 + 64 * dem));
    out[0] = Math.round(out[0] + (248 - out[0]) * boost);
    out[1] = Math.round(out[1] + (242 - out[1]) * boost);
    out[2] = Math.round(out[2] + (232 - out[2]) * boost);
  }
  out[3] = params.aFixed === 255 ? 255
    : Math.max(0, Math.min(255, Math.round(params.aFixed ?? value * (params.aScale ?? 15))));
  return out;
}

export function paletteColor({ mu, numIterations, dem = 0 }, params) {
  return samplePalette(params.useMu === false ? numIterations : mu, params, [0, 0, 0, 0], dem);
}
